var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc wordcloud
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Text } from '../../element';
import { SeriesModel } from '../../model';
import { LegendType } from '../../interface/legend';
import WordCloud from '../../../third-party/wordcloud/wordcloud';
import { degToRad, interpolateNumber } from '../../utils/math';
import { registerChart } from '../index';
var EVENT_STOP = 'wordcloudstop';
/**
 * default
 */
var WordCloudModel = /** @class */ (function (_super) {
    __extends(WordCloudModel, _super);
    function WordCloudModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'wordcloud3';
        _this.legendIcon = 'rect';
        _this.legendType = LegendType.DATUM_LEGEND;
        _this._ready = false;
        _this.handleWordCloudDraw = function (e) {
            var renderer = _this.getRenderObj();
            var drawn = e.detail.drawn;
            var _a = _this.getFacetBBox(), top = _a.top, left = _a.left;
            if (drawn) {
                var gridSize = _this.option.gridSize;
                var dataIndex_1 = lodash.findIndex(_this.state.points, function (p) { return p.name === drawn.item[0]; });
                var point = _this.state.points[dataIndex_1];
                var needAnimation = _this.state.needAnimation;
                var scale = [1 / drawn.info.mu, 1 / drawn.info.mu];
                var text = new Text(__assign(__assign(__assign({ key: _this.getKey("text-" + dataIndex_1), display: _this.visible() && point.visible, ref: point.shapeRef, x: drawn.info.fillTextOffsetX, y: drawn.info.fillTextOffsetY + drawn.info.fontSize / 2, text: drawn.item[0], fill: point.color }, _this.getTextStyle(_this.option.textStyle)), (point.selected ? _this.getTextStyle(_this.option.selected.textStyle) : null)), { textBaseline: 'middle', fontSize: drawn.info.fontSize, scale: needAnimation ? [0, 0] : scale, position: [
                        (drawn.gx + drawn.info.gw / 2) * gridSize + left,
                        (drawn.gy + drawn.info.gh / 2) * gridSize + top,
                    ], rotation: -drawn.rotateDeg, onEvent: function (event) {
                        return _this.handleUIEvent(event, 'point', {
                            title: _this.option.name,
                            point: _this.getWrappedPoint(dataIndex_1),
                        });
                    } }));
                renderer.add(text);
                if (needAnimation) {
                    text.animateTo({
                        scale: [1 / drawn.info.mu, 1 / drawn.info.mu],
                    });
                }
            }
        };
        _this._handleWordCloudStop = function () {
            _this.chart.dispatchAction({
                type: EVENT_STOP,
            });
        };
        return _this;
    }
    WordCloudModel.prototype.getDefaultOption = function () {
        return {
            type: 'wordCloud',
            name: '',
            encode: {
                name: '',
                value: '',
            },
            sizeRange: [12, 60],
            maskImage: null,
            shape: 'circle',
            gridSize: 8,
            minRotation: -90,
            maxRotation: 90,
            rotateRatio: 0.5,
            drawOutOfBound: false,
            rotationSteps: 4,
            wait: 0,
            ellipticity: 0.65,
            textStyle: {
                color: null,
                fontSize: null,
                fontWeight: 'normal',
                cursor: 'pointer',
                shadowBlur: 0,
            },
            hover: {
                textStyle: {
                    color: null,
                    fontSize: null,
                    shadowBlur: 8,
                    shadowColor: 'rgba(0, 0, 0, .2)',
                },
            },
            selected: {
                textStyle: {
                    color: null,
                    fontSize: null,
                },
            },
            label: null,
            visible: true,
            showLegend: false,
            selectedMode: false,
            hoverBrightness: 0,
        };
    };
    WordCloudModel.prototype.init = function () {
        var canvas = document.createElement('canvas');
        this._canvas = canvas;
        this._canvas.addEventListener('wordclouddrawn', this.handleWordCloudDraw);
        this._canvas.addEventListener(EVENT_STOP, this._handleWordCloudStop);
        // document.querySelector('#main').appendChild(canvas);
    };
    WordCloudModel.prototype.getInitialState = function () {
        return {
            points: [],
            needAnimation: this.needAnimation(),
        };
    };
    WordCloudModel.prototype.beforeRender = function () {
        var _this = this;
        var dataset = this._dataset;
        var valueField = this.option.encode.value;
        var valueRange = dataset.getExtreme(valueField);
        var sizeRange = this.option.sizeRange;
        var points = dataset.mapData(function (record, dataIndex) {
            var encode = _this._encode.getEncodeData(record, dataIndex);
            var size = interpolateNumber(encode.value, valueRange, sizeRange);
            return {
                encode: encode,
                name: encode.name,
                value: encode.value,
                size: size,
                color: encode.color,
                visible: !_this.invisibleItems.has(dataIndex),
                selected: _this.selectedItems.has(record),
                shapeRef: createRef(),
            };
        });
        this._ready = !this.option.maskImage;
        this.setState({ points: points });
        this.initCanvas();
    };
    WordCloudModel.prototype.initCanvas = function () {
        var _this = this;
        var _a = this.getFacetBBox(), width = _a.width, height = _a.height;
        this._canvas.width = width;
        this._canvas.height = height;
        var maskCanvas = this._canvas;
        var ctx = this._canvas.getContext('2d');
        if (this.option.maskImage) {
            var img_1 = new Image();
            img_1.crossOrigin = 'anonymous';
            // https://github.com/timdream/wordcloud2.js/blob/c236bee60436e048949f9becc4f0f67bd832dc5c/index.js#L233
            img_1.src = this.option.maskImage;
            img_1.onload = function () {
                _this._ready = true;
                ctx.drawImage(img_1, 0, 0, img_1.width, img_1.height, 0, 0, maskCanvas.width, maskCanvas.height);
                var imageData = ctx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
                var newImageData = ctx.createImageData(imageData);
                for (var i = 0; i < imageData.data.length; i += 4) {
                    var tone = imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2];
                    var alpha = imageData.data[i + 3];
                    if (alpha < 128 || tone > 200 * 3) {
                        // area not to draw
                        newImageData.data[i] = newImageData.data[i + 1] = newImageData.data[i + 2] = 255;
                        newImageData.data[i + 3] = 0;
                    }
                    else {
                        // area to draw
                        newImageData.data[i] = newImageData.data[i + 1] = newImageData.data[i + 2] = 255;
                        newImageData.data[i + 3] = 255;
                    }
                }
                ctx.putImageData(newImageData, 0, 0);
                if (!_this.disposed) {
                    _this.render();
                }
            };
        }
    };
    WordCloudModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        var _a;
        var textStyle = this.option.textStyle;
        var selectedTextStyle = this.option.selected.textStyle;
        var hoverTextStyle = this.option.hover.textStyle;
        var point = this.state.points[dataIndex];
        (_a = point.shapeRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(__assign(__assign(__assign({ fill: active ? this.getHoverColor(point.color) : point.color }, this.getTextStyle(textStyle)), (point.selected ? this.getTextStyle(selectedTextStyle) : null)), (active ? this.getTextStyle(hoverTextStyle) : null)));
    };
    WordCloudModel.prototype.toggleSelectPoint = function (dataIndex, selected) {
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var textStyle = this.option.textStyle;
        var selectedTextStyle = this.option.selected.textStyle;
        var point = this.state.points[dataIndex];
        point.shapeRef.current.setAttr(__assign({ fill: point.color }, this.getTextStyle(selected ? selectedTextStyle : textStyle)));
    };
    WordCloudModel.prototype.getWordCloudOptions = function () {
        var textStyle = this.getTextStyle(this.option.textStyle);
        return {
            list: this.state.points
                .map(function (point) { return [point.name, point.size]; })
                .sort(function (a, b) { return b[1] - a[1]; }),
            shape: this.option.shape,
            clearCanvas: !this.option.maskImage,
            gridSize: this.option.gridSize,
            minRotation: degToRad(this.option.minRotation),
            maxRotation: degToRad(this.option.maxRotation),
            rotateRatio: this.option.rotateRatio,
            rotationSteps: this.option.rotationSteps,
            drawOutOfBound: this.option.drawOutOfBound,
            fontWeight: textStyle.fontWeight,
            fontFamily: textStyle.fontFamily,
            shuffle: false,
            shrinkToFit: false,
            wait: this.option.wait,
            ellipticity: this.option.ellipticity,
        };
    };
    WordCloudModel.prototype.beforeDestroy = function () {
        this._canvas.removeEventListener('wordclouddrawn', this.handleWordCloudDraw);
        this._canvas.removeEventListener(EVENT_STOP, this._handleWordCloudStop);
        delete this._canvas;
    };
    WordCloudModel.prototype.render = function () {
        if (this._ready) {
            WordCloud(this._canvas, this.getWordCloudOptions());
        }
        return new Group({
            key: this.getKey('wordcloud'),
        });
    };
    WordCloudModel.dependencies = ['legend'];
    return WordCloudModel;
}(SeriesModel));
export default WordCloudModel;
registerChart('wordCloud', WordCloudModel);
